
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import BottomNavigation from "@/components/mobile/BottomNavigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { Suspense, lazy } from "react";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TranslationPromptBanner } from "@/components/TranslationPromptBanner";

// Lazy load all pages including Index for better code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin and secondary pages
const Search = lazy(() => import("./pages/Search"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminListingForm = lazy(() => import("./pages/AdminListingForm"));
const SubmitListing = lazy(() => import("./pages/SubmitListing"));
const AdminSubmissions = lazy(() => import("./pages/AdminSubmissions"));
const AdminSubmissionEdit = lazy(() => import("./pages/AdminSubmissionEdit"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Settings = lazy(() => import("./pages/Settings"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));

// Lazy load advertiser pages
const Advertising = lazy(() => import("./pages/advertiser/Advertising"));
const AdvertiserOnboarding = lazy(() => import("./pages/advertiser/AdvertiserOnboarding"));
const AdvertiserDashboard = lazy(() => import("./pages/advertiser/AdvertiserDashboard"));
const CreateCampaign = lazy(() => import("./pages/advertiser/CreateCampaign"));
const EditCampaign = lazy(() => import("./pages/advertiser/EditCampaign"));

// Optimized loading component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const isMobile = useIsMobile();
  
  return (
    <>
      <TranslationPromptBanner />
      <Routes>
        <Route path="/" element={<Suspense fallback={<PageLoader />}><Index /></Suspense>} />
        <Route path="/search" element={<Suspense fallback={<PageLoader />}><Search /></Suspense>} />
        <Route path="/auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
        <Route path="/profile" element={<Suspense fallback={<PageLoader />}><Profile /></Suspense>} />
        <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
        <Route path="/admin/dashboard" element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
        <Route path="/admin/add-listing" element={<Suspense fallback={<PageLoader />}><AdminListingForm /></Suspense>} />
        <Route path="/admin/edit-listing/:id" element={<Suspense fallback={<PageLoader />}><AdminListingForm /></Suspense>} />
        <Route path="/submit-listing" element={<Suspense fallback={<PageLoader />}><SubmitListing /></Suspense>} />
        <Route path="/admin/submissions" element={<Suspense fallback={<PageLoader />}><AdminSubmissions /></Suspense>} />
        <Route path="/admin/submissions/:id/edit" element={<Suspense fallback={<PageLoader />}><AdminSubmissionEdit /></Suspense>} />
        {/* Advertiser routes */}
          <Route path="/advertising" element={<Suspense fallback={<PageLoader />}><Advertising /></Suspense>} />
          <Route path="/advertiser/onboarding" element={<Suspense fallback={<PageLoader />}><AdvertiserOnboarding /></Suspense>} />
          <Route path="/advertiser/dashboard" element={<Suspense fallback={<PageLoader />}><AdvertiserDashboard /></Suspense>} />
        <Route path="/advertiser/campaigns/new" element={<Suspense fallback={<PageLoader />}><CreateCampaign /></Suspense>} />
        <Route path="/advertiser/campaigns/:id" element={<Suspense fallback={<PageLoader />}><EditCampaign /></Suspense>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="/about" element={<Suspense fallback={<PageLoader />}><AboutUs /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsOfService /></Suspense>} />
        <Route path="/cookies" element={<Suspense fallback={<PageLoader />}><CookiePolicy /></Suspense>} />
        <Route path="/how-it-works" element={<Suspense fallback={<PageLoader />}><HowItWorks /></Suspense>} />
        <Route path="/settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
        <Route path="/notification-settings" element={<Suspense fallback={<PageLoader />}><NotificationSettings /></Suspense>} />
        <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
      </Routes>
      {isMobile && <BottomNavigation />}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
